import { Component, OnInit, Input, Output, EventEmitter, forwardRef, ElementRef, HostListener, ViewChild, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIf, NgFor, NgClass, DatePipe } from '@angular/common';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { TooltipDirective } from '../ni-tooltip/ni-tooltip.directive';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
interface RippleState {
  show: boolean;
  x: number;
  y: number;
  target?: string;
}

type TimeField = 'hour' | 'minute' | 'second';
type RangeTimeField = 'hour' | 'minute';
type RangeType = 'start' | 'end';
type AmPmPeriod = 'AM' | 'PM';
type NavigationDirection = 'prev' | 'next';

@Component({
  selector: 'NiDatePicker',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, TooltipDirective, NiIconComponent],
  animations: [
    trigger('calendarAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
          style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
          style({ opacity: 0, transform: 'translateY(10px)' }))
      ])
    ]),
    trigger('monthAnimation', [
      state('void', style({ opacity: 0, transform: 'translateY(30px)' })),
      state('*', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('* => void', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('void => *', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9)' }),
        animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
          style({ opacity: 1, transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        animate('100ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
          style({ opacity: 0, transform: 'scale(0.9)' }))
      ])
    ]),
    trigger('rippleAnimation', [
      transition(':enter', [
        style({ transform: 'scale(0)', opacity: 0.5 }),
        animate('400ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
          style({ transform: 'scale(2)', opacity: 0 }))
      ])
    ])
  ],
  templateUrl: './ni-date-picker.component.html',
  styleUrls: ['./ni-date-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NiDatePickerComponent),
      multi: true
    },
    DatePipe
  ]
})
export class NiDatePickerComponent implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor {
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() hasError: boolean = false;
  @Input() errorMessage: string = '';
  @Input() enableTime: boolean = false;
  @Input() enableSeconds: boolean = false;
  @Input() use24Hour: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() disabledDates?: Date[];
  @Input() firstDayOfWeek: 0 | 1 = 0;
  @Input() format: string = 'yyyy-MM-dd';
  @Input() timeFormat: string = 'h:mm a';
  @Input() placeholder: string = 'Select Date';
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() position: 'auto' | 'above' | 'below' = 'auto';
  @Input() inline: boolean = false;
  @Input() showWeekNumbers: boolean = false;
  @Input() allowInput: boolean = false;
  @Input() clearable: boolean = true;
  @Input() closeOnSelect: boolean = true;
  @Input() disabled: boolean = false;
  @Input() yearRange: number = 20;
  @Input() rangeMode: boolean = false;
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() date: Date | null = null;
  @Input() defaultViewDate: Date | null = null;
  @Input() containerStyle: string = '';
  @Input() inputStyle: string = '';
  @Input() clearIcon: string = 'Close';
  @Input() clearIconClass: string = 'w-5 h-5 text-white';
  @Input() leadingIcon: string = 'DateRange';
  @Input() leadingIconClass: string = 'w-5 h-5 text-[#444955]';
  @Output() dateChange = new EventEmitter<Date | null>();
  @Output() rangeChange = new EventEmitter<{start: Date | null, end: Date | null}>();
  @ViewChild('datePickerContainer') datePickerContainer!: ElementRef;
  @ViewChild('inputElement') inputElement!: ElementRef;
  @ViewChild('yearListContainer') yearListContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('dayWheel') set dayWheelRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.dayWheel = ref;
    this.scrollWheelToSelection();
  }
  @ViewChild('monthWheel') set monthWheelRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.monthWheel = ref;
    this.scrollWheelToSelection();
  }
  @ViewChild('yearWheel') set yearWheelRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.yearWheel = ref;
    this.scrollWheelToSelection();
  }
  @ViewChild('dayWheelSelect') dayWheelSelect?: ElementRef<HTMLSelectElement>;
  @ViewChild('monthWheelSelect') monthWheelSelect?: ElementRef<HTMLSelectElement>;
  @ViewChild('yearWheelSelect') yearWheelSelect?: ElementRef<HTMLSelectElement>;

  selectedDate: Date | null = null;
  currentMonth: number = 0;
  currentYear: number = 0;
  isOpen: boolean = false;
  calendarDays: Date[][] = [];
  inputValue: string = '';
  monthDropdownOpen: boolean = false;
  yearDropdownOpen: boolean = false;
  currentMonthViewState: string = 'initial';
  rippleState: RippleState = {
    show: false,
    x: 0,
    y: 0,
    target: ''
  };
  calendarPosition: 'above' | 'below' = 'below';

  months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  weekdayLabels: string[] = [];
  yearOptions: number[] = [];
  tempDate: Date | null = null;
  dayOptions: number[] = [];
  private viewInitialized = false;
  private dayWheel?: ElementRef<HTMLDivElement>;
  private monthWheel?: ElementRef<HTMLDivElement>;
  private yearWheel?: ElementRef<HTMLDivElement>;
  // Static counter for generating unique IDs across component instances
  // Uses cryptographically secure random number generator for security best practices.
  // The combination of counter + random ensures uniqueness.
  private static idCounterValue = 0;
  private static getNextIdCounter(): number {
    return ++NiDatePickerComponent.idCounterValue;
  }
  
  private static generateSecureRandomString(): string {
    let cryptoObj: Crypto | null = null;
    if (typeof crypto !== 'undefined') {
      cryptoObj = crypto;
    } else if (typeof window !== 'undefined' && window.crypto) {
      cryptoObj = window.crypto;
    }
    
    if (cryptoObj && 'getRandomValues' in cryptoObj) {
      const array = new Uint32Array(1);
      cryptoObj.getRandomValues(array);
      return array[0].toString(36).substring(2, 9);
    }
    // Fallback: should not happen in modern browsers, but included for safety
    return Date.now().toString(36).substring(2, 9);
  }
  
  inputId: string =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `date-picker-${Date.now()}-${NiDatePickerComponent.getNextIdCounter()}-${NiDatePickerComponent.generateSecureRandomString()}`;

  // ControlValueAccessor requirements
  private onChange: any = () => {};
  private onTouched: any = () => {};

  private selectionStep: 'start' | 'end' = 'start';

  constructor(private readonly datePipe: DatePipe) {
    // Set today's date if none is provided
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.scrollWheelToSelection();
  }

  ngOnInit(): void {
    // Generate weekday labels based on first day of week
    this.generateWeekdayLabels();
    this.applyDefaultViewDateIfNeeded();
    
    // Generate calendar days
    this.generateCalendarDays();
    
    // Generate year options
    this.generateYearOptions();

    this.initializeWheelState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['date']) {
      this.selectedDate = changes['date'].currentValue ? new Date(changes['date'].currentValue) : null;
      this.updateInputValue();
      if (!this.selectedDate) {
        this.applyDefaultViewDateIfNeeded(true);
        this.generateCalendarDays();
        return;
      }
      this.currentMonth = this.selectedDate.getMonth();
      this.currentYear = this.selectedDate.getFullYear();
      this.generateCalendarDays();
    } else if (changes['startDate'] || changes['endDate']) {
      this.updateInputValue();
    }
  }

  // Determine the best position for the calendar
  determineCalendarPosition(): void {
    if (this.position !== 'auto') {
      this.calendarPosition = this.position;
      return;
    }

    if (!this.datePickerContainer) return;

    const rect = this.datePickerContainer.nativeElement.getBoundingClientRect();
    const calendarHeight = 280; // Approximate height of calendar
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;

    // If there's not enough space below but enough space above
    if (spaceBelow < calendarHeight && rect.top > calendarHeight) {
      this.calendarPosition = 'above';
      return;
    }
    this.calendarPosition = 'below';
  }

  // Start ripple animation
  startRipple(event?: Event, target: string = ''): void {
    if (event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const x = (event as MouseEvent).clientX - rect.left;
      const y = (event as MouseEvent).clientY - rect.top;
      
      this.rippleState = {
        show: true,
        x,
        y,
        target
      };
    } else {
      this.rippleState = {
        show: true,
        x: 0,
        y: 0,
        target
      };
    }

    setTimeout(() => {
      if (this.rippleState.target === target) {
        this.rippleState.show = false;
      }
    }, 400);
  }

  // Toggle dropdowns
  toggleMonthDropdown(event: Event): void {
    event.stopPropagation();
    this.monthDropdownOpen = !this.monthDropdownOpen;
    this.yearDropdownOpen = false; // Close other dropdown
    this.startRipple();
  }

  toggleYearDropdown(event: Event): void {
    event.stopPropagation();
    this.yearDropdownOpen = !this.yearDropdownOpen;
    this.monthDropdownOpen = false; // Close other dropdown
    this.startRipple();
    if (this.yearDropdownOpen) {
      setTimeout(() => this.scrollActiveYearIntoView(), 0);
    }
  }

  // Update month selection method
  selectMonth(month: number, event: Event): void {
    event.stopPropagation();
    
    // Trigger animation on month change
    this.currentMonthViewState = 'void';
    setTimeout(() => {
      this.currentMonth = month;
      this.monthDropdownOpen = false;
      
      // Update selected date if one exists
      if (this.selectedDate) {
        const newDate = new Date(this.selectedDate);
        const currentDay = newDate.getDate();
        newDate.setMonth(month);
        
        // Handle case where the day doesn't exist in the new month (e.g., Jan 31 -> Feb)
        const daysInNewMonth = new Date(this.currentYear, month + 1, 0).getDate();
        if (currentDay > daysInNewMonth) {
          newDate.setDate(daysInNewMonth);
        }
        
        this.selectedDate = newDate;
        this.updateInputValue();
        this.onChange(this.selectedDate);
        this.dateChange.emit(this.selectedDate);
      }
      
      this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);
    
    this.startRipple();
  }

  // Update year selection method
  selectYear(year: number, event: Event): void {
    event.stopPropagation();
    
    // Trigger animation on year change
    this.currentMonthViewState = 'void';
    setTimeout(() => {
      this.currentYear = year;
      this.yearDropdownOpen = false;
      
      // Update selected date if one exists
      if (this.selectedDate) {
        const newDate = new Date(this.selectedDate);
        const currentDay = newDate.getDate();
        const currentMonth = newDate.getMonth();
        newDate.setFullYear(year);
        
        // Handle leap year case (Feb 29 -> Feb 28 in non-leap years)
        const daysInNewMonth = new Date(year, currentMonth + 1, 0).getDate();
        if (currentDay > daysInNewMonth) {
          newDate.setDate(daysInNewMonth);
        }
        
        this.selectedDate = newDate;
        this.updateInputValue();
        this.onChange(this.selectedDate);
        this.dateChange.emit(this.selectedDate);
      }
      
      this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);
    
    this.startRipple();
  }

  // Generate year options for the dropdown
  generateYearOptions(): void {
    this.yearOptions = [];
    
    // Generate all years from 1900 to current year (don't allow future years)
    const startYear = 1900;
    const endYear = new Date().getFullYear();
    
    for (let i = startYear; i <= endYear; i++) {
      this.yearOptions.push(i);
    }
  }

  // Generate weekday labels (Sun-Sat or Mon-Sun)
  generateWeekdayLabels(): void {
    const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    
    this.weekdayLabels = [
      ...weekdays.slice(this.firstDayOfWeek),
      ...weekdays.slice(0, this.firstDayOfWeek)
    ];
  }

  // Generate calendar days
  generateCalendarDays(): void {
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
    
    // Determine the first day to show in the calendar
    const firstDayToShow = new Date(firstDayOfMonth);
    let dayOfWeek = firstDayOfMonth.getDay();
    
    // Adjust for first day of week
    if (dayOfWeek < this.firstDayOfWeek) {
      dayOfWeek += 7;
    }
    
    firstDayToShow.setDate(firstDayToShow.getDate() - (dayOfWeek - this.firstDayOfWeek));
    
    // Create 6 weeks (42 days) to ensure enough space
    this.calendarDays = [];
    let currentWeek: Date[] = [];
    
    for (let i = 0; i < 42; i++) {
      const day = new Date(firstDayToShow);
      day.setDate(firstDayToShow.getDate() + i);
      
      currentWeek.push(day);
      
      if (currentWeek.length === 7) {
        this.calendarDays.push(currentWeek);
        currentWeek = [];
      }
    }
  }

  initializeWheelState(): void {
    const baseDate = this.getBaseDate();
    
    if (!baseDate) {
      this.initializeWheelStateWithoutDate();
      return;
    }

    const validatedDate = this.validateAndConstrainDate(baseDate);
    this.setTempDateFromBase(validatedDate);
  }

  private getBaseDate(): Date | null {
    return this.selectedDate ?? this.date ?? this.defaultViewDate ?? null;
  }

  private initializeWheelStateWithoutDate(): void {
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.tempDate = null;
    this.refreshWheelMetrics();
    
    setTimeout(() => {
      this.resetWheelScrollPositions();
    }, 0);
  }

  private validateAndConstrainDate(baseDate: Date): Date {
    let validatedDate = new Date(baseDate);

    if (this.minDate && validatedDate < this.minDate) {
      validatedDate = new Date(this.minDate);
    }

    if (this.maxDate && validatedDate > this.maxDate) {
      validatedDate = new Date(this.maxDate);
    }

    const currentYear = new Date().getFullYear();
    if (validatedDate.getFullYear() > currentYear) {
      validatedDate = new Date(currentYear, validatedDate.getMonth(), validatedDate.getDate());
    }

    return validatedDate;
  }

  private setTempDateFromBase(baseDate: Date): void {
    this.tempDate = new Date(baseDate);
    this.currentMonth = this.tempDate.getMonth();
    this.currentYear = this.tempDate.getFullYear();
    this.refreshWheelMetrics();
    this.scrollWheelToSelection();
  }

  refreshWheelMetrics(): void {
    const reference = this.tempDate ?? new Date();
    const totalDays = this.getDaysInMonth(reference.getFullYear(), reference.getMonth());
    this.dayOptions = Array.from({ length: totalDays }, (_, index) => index + 1);
    this.scrollWheelToSelection();
  }

  getDaysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  getTempDay(): number {
    return this.tempDate ? this.tempDate.getDate() : -1;
  }

  getTempMonth(): number {
    return this.tempDate ? this.tempDate.getMonth() : -1;
  }

  getTempYear(): number {
    return this.tempDate ? this.tempDate.getFullYear() : -1;
  }

  selectTempDay(day: number): void {
    this.updateTempDate({ day });
  }

  selectTempMonth(month: number): void {
    this.updateTempDate({ month });
  }

  selectTempYear(year: number): void {
    this.updateTempDate({ year });
  }

  updateTempDate(partial: { day?: number; month?: number; year?: number }): void {
    // If no tempDate exists, use currentMonth/currentYear (for display) with day 1
    const current = this.tempDate 
      ? new Date(this.tempDate) 
      : new Date(this.currentYear, this.currentMonth, 1);
    let nextYear = partial.year ?? current.getFullYear();
    const nextMonth = partial.month ?? current.getMonth();
    let nextDay = partial.day ?? (this.tempDate ? current.getDate() : 1);

    // Don't allow years beyond current year
    const currentYear = new Date().getFullYear();
    if (nextYear > currentYear) {
      nextYear = currentYear;
    }

    const monthDays = this.getDaysInMonth(nextYear, nextMonth);
    if (nextDay > monthDays) {
      nextDay = monthDays;
    }

    const candidate = new Date(nextYear, nextMonth, nextDay);
    if (this.isDisabled(candidate)) {
      return;
    }

    this.tempDate = candidate;
    this.currentMonth = nextMonth;
    this.currentYear = nextYear;
    this.refreshWheelMetrics();
    this.scrollWheelToSelection();
    this.syncSelectElements();
  }

  private syncSelectElements(): void {
    // Sync the hidden select elements with the current temp date for accessibility
    if (this.dayWheelSelect?.nativeElement) {
      this.dayWheelSelect.nativeElement.value = this.getTempDay().toString();
    }
    if (this.monthWheelSelect?.nativeElement) {
      this.monthWheelSelect.nativeElement.value = this.getTempMonth().toString();
    }
    if (this.yearWheelSelect?.nativeElement) {
      this.yearWheelSelect.nativeElement.value = this.getTempYear().toString();
    }
  }

  confirmTempSelection(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (!this.tempDate) {
      return;
    }

    const confirmedDate = new Date(this.tempDate);

    if (this.isDisabled(confirmedDate)) {
      return;
    }

    if (this.enableTime && this.selectedDate) {
      confirmedDate.setHours(
        this.selectedDate.getHours(),
        this.selectedDate.getMinutes(),
        this.enableSeconds ? this.selectedDate.getSeconds() : 0
      );
    }

    this.selectedDate = confirmedDate;
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);

    if (!this.inline) {
      this.closeCalendar();
    }
  }

  private scrollWheelToSelection(): void {
    if (!this.viewInitialized) return;
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!this.tempDate) {
          this.resetWheelScrollPositions();
          return;
        }
        
        this.scrollToSelectedValues();
      }, 50);
    });
  }

  private resetWheelScrollPositions(): void {
    if (this.dayWheel) {
      this.dayWheel.nativeElement.scrollTop = 0;
    }
    if (this.monthWheel) {
      this.monthWheel.nativeElement.scrollTop = 0;
    }
    if (this.yearWheel) {
      this.yearWheel.nativeElement.scrollTop = 0;
    }
  }

  private scrollToSelectedValues(): void {
    this.scrollToDay();
    this.scrollToMonth();
    this.scrollToYear();
  }

  private scrollToDay(): void {
    const tempDay = this.getTempDay();
    if (tempDay >= 1) {
      this.scrollToCurrentOption(this.dayWheel, tempDay - 1);
    }
  }

  private scrollToMonth(): void {
    const tempMonth = this.getTempMonth();
    if (tempMonth >= 0) {
      this.scrollToCurrentOption(this.monthWheel, tempMonth);
    }
  }

  private scrollToYear(): void {
    const tempYear = this.getTempYear();
    if (tempYear >= 0) {
      const yearIndex = this.yearOptions.findIndex(year => year === tempYear);
      if (yearIndex >= 0) {
        this.scrollToCurrentOption(this.yearWheel, yearIndex);
      }
    }
  }

  private scrollToCurrentOption(container?: ElementRef<HTMLDivElement>, optionIndex?: number): void {
    if (!container || optionIndex === undefined || optionIndex < 0) return;
    
    const target = container.nativeElement.querySelector<HTMLElement>(`[data-option-index="${optionIndex}"]`);
    if (target) {
      const containerElement = container.nativeElement;
      const topPadding = 160; // Match the pt-[160px] padding
      
      // Get the offset position of the target relative to the container
      const targetOffsetTop = target.offsetTop;
      
      // Calculate scroll position: target position minus the top padding
      // This ensures the selected item appears below the padding area
      const scrollPosition = targetOffsetTop - topPadding;
      
      containerElement.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }

  // Update the input value based on selected date and format
  updateInputValue(): void {
    if (this.rangeMode) {
      this.updateRangeInputValue();
    } else {
      this.updateSingleInputValue();
    }
  }

  private updateRangeInputValue(): void {
    let formatted = '';
    
    if (this.startDate) {
      formatted = this.formatDateTime(this.startDate);
      
      if (this.endDate) {
        formatted += ' - ' + this.formatDateTime(this.endDate);
      } else if (this.selectionStep === 'end') {
        formatted += ' - ...';
      }
    }
    
    this.inputValue = formatted;
  }

  private updateSingleInputValue(): void {
    if (!this.selectedDate) {
      this.inputValue = '';
      return;
    }

    this.inputValue = this.formatDateTime(this.selectedDate);
  }

  // Handle keyboard events on calendar icon
  onCalendarIconKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleCalendar();
    }
  }

  // Handle keyboard events on overlay (Escape to close)
  handleOverlayKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.inline) {
      event.preventDefault();
      this.closeCalendar(event);
    }
  }

  // Handle keyboard events on container div
  handleContainerKeyDown(event: KeyboardEvent): void {
    // Allow default behavior for input focus
    if (event.target instanceof HTMLInputElement) {
      return;
    }
    // Handle Enter/Space on container to open calendar
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.disabled) {
        this.toggleCalendar();
      }
    }
  }

  // Handle keyboard events on wheel containers
  handleWheelKeyDown(event: KeyboardEvent, wheelType: 'day' | 'month' | 'year'): void {
    // Allow arrow keys to scroll the wheel
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) {
      // Let the default scroll behavior happen
      return;
    }
    // Prevent other keys from triggering default behavior
    if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
    }
  }

  // Toggle the calendar open/closed
  toggleCalendar(): void {
    if (this.disabled) return;
    
    if (!this.isOpen) {
      // Determine position before opening
      this.determineCalendarPosition();
      this.initializeWheelState();
      this.isOpen = true;
      this.currentMonthViewState = 'initial';
      // Ensure scroll positions are reset to top after calendar opens
      setTimeout(() => {
        if (!this.tempDate) {
          if (this.dayWheel) {
            this.dayWheel.nativeElement.scrollTop = 0;
          }
          if (this.monthWheel) {
            this.monthWheel.nativeElement.scrollTop = 0;
          }
          if (this.yearWheel) {
            this.yearWheel.nativeElement.scrollTop = 0;
          }
        }
        this.scrollWheelToSelection();
      }, 100);
    } else {
      this.isOpen = false;
    }
  }

  // Select a date
  selectDate(date: Date): void {
    if (this.isDisabled(date) || !this.isInCurrentMonth(date)) return;
    
    let newDate = new Date(date);
    
    // Preserve time if already selected
    if (this.selectedDate && this.enableTime) {
      newDate.setHours(
        this.selectedDate.getHours(),
        this.selectedDate.getMinutes(),
        this.enableSeconds ? this.selectedDate.getSeconds() : 0
      );
    } else {
      // Set default time
      newDate.setHours(0, 0, 0, 0);
    }
    
    this.selectedDate = newDate;
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
    
    // Close the calendar if time picking is not enabled or closeOnSelect is true
    if ((!this.enableTime || this.closeOnSelect) && !this.inline) {
      setTimeout(() => {
      this.isOpen = false;
      }, 200); // Short delay for animation
    }
    
    this.startRipple(undefined, 'day-'+date.getTime());
  }

  // Clear the selected date
  clearDate(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    this.selectedDate = null;
    this.inputValue = '';
    this.onChange(null);
    this.dateChange.emit(null);
    this.startRipple();
  }

  // Go to today
  goToToday(): void {
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.generateCalendarDays();
    
    if (!this.rangeMode) {
      // For single date mode
      this.selectedDate = new Date(today);
      this.onChange(this.selectedDate);
      this.dateChange.emit(this.selectedDate);
    }
    
    this.updateInputValue();
  }

  // Navigate to previous month
  prevMonth(): void {
    this.currentMonthViewState = 'void';
    
    setTimeout(() => {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);
  }

  // Navigate to next month
  nextMonth(): void {
    this.currentMonthViewState = 'void';
    
    setTimeout(() => {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);
  }

  // Check if a date is today
  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  // Check if a date is selected
  isSelected(date: Date): boolean {
    if (!this.selectedDate) return false;
    
    return (
      date.getDate() === this.selectedDate.getDate() &&
      date.getMonth() === this.selectedDate.getMonth() &&
      date.getFullYear() === this.selectedDate.getFullYear()
    );
  }

  // Check if a date is in the current month
  isInCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.currentMonth;
  }

  // Check if a date is disabled
  isDisabled(date: Date): boolean {
    // Check min date
    if (this.minDate) {
      const minDate = new Date(this.minDate);
      minDate.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      if (checkDate < minDate) {
        return true;
      }
    }
    
    // Check max date
    if (this.maxDate) {
      const maxDate = new Date(this.maxDate);
      maxDate.setHours(23, 59, 59, 999);
      const checkDate = new Date(date);
      checkDate.setHours(23, 59, 59, 999);
      if (checkDate > maxDate) {
        return true;
      }
    }
    
    // Check disabled dates
    if (this.disabledDates) {
      return this.disabledDates.some(disabledDate => 
        date.getDate() === disabledDate.getDate() &&
        date.getMonth() === disabledDate.getMonth() &&
        date.getFullYear() === disabledDate.getFullYear()
      );
    }
    
    return false;
  }

  // Get week number for a date
  getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }

  // Handle hour change in time picker
  onHourChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let hours = parseInt(input.value, 10);
    
    if (isNaN(hours)) return;
    
    if (!this.use24Hour) {
      // Convert 12h format to 24h internally
      const isPM = this.selectedDate ? this.selectedDate.getHours() >= 12 : false;
      if (hours === 12) {
        hours = isPM ? 12 : 0;
      } else if (isPM) {
        hours += 12;
      }
    }
    
    if (!this.selectedDate) {
      const now = new Date();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, 0, 0);
    } else {
      this.selectedDate.setHours(hours);
    }
    
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Handle minute change in time picker
  onMinuteChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const minutes = parseInt(input.value, 10);
    
    if (isNaN(minutes)) return;
    
    if (!this.selectedDate) {
      const now = new Date();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, minutes, 0);
    } else {
      this.selectedDate.setMinutes(minutes);
    }
    
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Handle AM/PM change in time picker
  onAmPmChange(event: Event): void {
    if (!this.selectedDate) return;
    
    const select = event.target as HTMLSelectElement;
    const isPM = select.value === 'PM';
    const hours = this.selectedDate.getHours();
    const is24PM = hours >= 12;
    
    if (isPM && !is24PM) {
      this.selectedDate.setHours(hours + 12);
    } else if (!isPM && is24PM) {
      this.selectedDate.setHours(hours - 12);
    }
    
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Handle direct input change
  onInputChange(event: Event): void {
    if (!this.allowInput) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    try {
      if (value === '') {
        this.clearDate();
        return;
      }
      
      const parsedDate = this.parseInputValue(value);
      if (parsedDate && !this.isDisabled(parsedDate)) {
        this.applyParsedDate(parsedDate);
      }
    } catch {
      // Invalid input
    }
  }

  private parseInputValue(value: string): Date | null {
    const parts = value.split(/[-/\s:]/);
    if (parts.length < 3) {
      return null;
    }
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }
    
    const newDate = new Date(year, month, day);
    
    if (this.enableTime && parts.length >= 5) {
      this.applyTimeToDate(newDate, parts);
    }
    
    return newDate;
  }

  private applyTimeToDate(date: Date, parts: string[]): void {
    const hours = parseInt(parts[3], 10);
    const minutes = parseInt(parts[4], 10);
    const seconds = parts.length >= 6 ? parseInt(parts[5], 10) : 0;
    
    if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds)) {
      date.setHours(hours, minutes, seconds);
    }
  }

  private applyParsedDate(date: Date): void {
    this.selectedDate = date;
    this.currentMonth = date.getMonth();
    this.currentYear = date.getFullYear();
    this.generateCalendarDays();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Helper to pad numbers with leading zero
  padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  // Helper to convert 24h to 12h format
  get12Hour(hour: number): number {
    if (hour === 0) return 12;
    if (hour > 12) return hour - 12;
    return hour;
  }

  // Get hours for display in the time picker
  getDisplayHours(): string {
    if (!this.selectedDate) return this.use24Hour ? '00' : '12';
    
    const hours = this.selectedDate.getHours();
    return this.use24Hour ? 
      this.padZero(hours) : 
      this.padZero(this.get12Hour(hours));
  }

  // ControlValueAccessor methods
  writeValue(value: Date | null): void {
    this.selectedDate = value;
    
    if (this.selectedDate) {
      this.currentMonth = this.selectedDate.getMonth();
      this.currentYear = this.selectedDate.getFullYear();
      this.generateCalendarDays();
    } else {
      this.applyDefaultViewDateIfNeeded(true);
      this.generateCalendarDays();
    }
    
    this.updateInputValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Add this method to close the calendar
  closeCalendar(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = false;
  }

  // Update the onTimeInputChange method to immediately enforce length and value limits
  onTimeInputChange(event: Event, field: TimeField): void {
    const input = event.target as HTMLInputElement;
    
    // First, truncate to maximum 2 digits
    if (input.value.length > 2) {
      input.value = input.value.slice(0, 2);
    }
    
    let value = parseInt(input.value, 10);
    
    if (isNaN(value)) {
      return;
    }
    
    // Apply range limits immediately during input
    const { minValue, maxValue } = this.getTimeFieldLimits(field);
    
    if (value < minValue) {
      value = minValue;
    }
    if (value > maxValue) {
      input.value = maxValue.toString();
      value = maxValue;
    }
    
    // Update the field with the valid value
    this.updateTimeField(value, field);
  }

  private getTimeFieldLimits(field: TimeField): { minValue: number; maxValue: number } {
    if (field === 'hour') {
      return {
        minValue: this.use24Hour ? 0 : 1,
        maxValue: this.use24Hour ? 23 : 12
      };
    }
    return { minValue: 0, maxValue: 59 };
  }

  // Validate time input on blur
  validateTimeInput(event: Event, field: TimeField): void {
    const input = event.target as HTMLInputElement;
    let value = parseInt(input.value, 10);
    
    if (isNaN(value)) {
      value = 0;
    }
    
    // Apply range limits based on field
    switch (field) {
      case 'hour':
        if (this.use24Hour) {
          value = Math.min(Math.max(value, 0), 23);
        } else {
          value = Math.min(Math.max(value, 1), 12);
        }
        break;
      case 'minute':
      case 'second':
        value = Math.min(Math.max(value, 0), 59);
        break;
    }
    
    // Update the field with the valid value
    this.updateTimeField(value, field);
    
    // Refresh display
    if (field === 'hour') {
      input.value = this.getDisplayHours();
    } else {
      input.value = this.padZero(value);
    }
  }

  // Update time field with new value
  updateTimeField(value: number, field: TimeField): void {
    if (!this.selectedDate) {
      this.selectedDate = new Date();
    }
    
    switch (field) {
      case 'hour':
        value = this.convertHourTo24HourFormat(value, this.selectedDate);
        this.selectedDate.setHours(value);
        break;
      case 'minute':
        this.selectedDate.setMinutes(value);
        break;
      case 'second':
        this.selectedDate.setSeconds(value);
        break;
    }
    
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Set AM or PM
  setAmPm(period: AmPmPeriod): void {
    if (!this.selectedDate) {
      this.selectedDate = new Date();
    }
    
    const hours = this.selectedDate.getHours();
    const isPM = hours >= 12;
    
    if (period === 'AM' && isPM) {
      this.selectedDate.setHours(hours - 12);
    } else if (period === 'PM' && !isPM) {
      this.selectedDate.setHours(hours + 12);
    }
    
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Prevent entering non-numeric characters in time inputs
  preventInvalidTimeChars(event: KeyboardEvent): void {
    // Prevent 'e', '+', '-' in number inputs
    if (['e', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  // Add this method to check if a navigation button is disabled
  isDisabledNavigationButton(direction: NavigationDirection): boolean {
    if (!this.minDate && !this.maxDate) return false;
    
    if (direction === 'prev') {
      return this.isPrevButtonDisabled();
    }
    return this.isNextButtonDisabled();
  }

  private isPrevButtonDisabled(): boolean {
    if (!this.minDate) return false;
    const testDate = new Date(this.currentYear, this.currentMonth);
    testDate.setMonth(testDate.getMonth() - 1);
    const minDateFirst = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
    return testDate < minDateFirst;
  }

  private isNextButtonDisabled(): boolean {
    if (!this.maxDate) return false;
    const testDate = new Date(this.currentYear, this.currentMonth);
    testDate.setMonth(testDate.getMonth() + 1);
    const maxDateFirst = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
    return testDate > maxDateFirst;
  }

  // Check if a date is the start date
  isStartDate(date: Date): boolean {
    if (!this.rangeMode || !this.startDate) return false;
    
    return (
      date.getDate() === this.startDate.getDate() &&
      date.getMonth() === this.startDate.getMonth() &&
      date.getFullYear() === this.startDate.getFullYear()
    );
  }

  // Check if a date is the end date
  isEndDate(date: Date): boolean {
    if (!this.rangeMode || !this.endDate) return false;
    
    return (
      date.getDate() === this.endDate.getDate() &&
      date.getMonth() === this.endDate.getMonth() &&
      date.getFullYear() === this.endDate.getFullYear()
    );
  }

  // Check if a date is in the range
  isInRange(date: Date): boolean {
    if (!this.rangeMode || !this.startDate || !this.endDate) return false;
    
    return date > this.startDate && date < this.endDate;
  }

  // Handle range selection
  handleRangeSelection(date: Date): void {
    if (this.isDisabled(date) || !this.isInCurrentMonth(date)) return;
    
    if (this.selectionStep === 'start' || (this.startDate && this.endDate)) {
      this.startNewRangeSelection(date);
    } else {
      this.completeRangeSelection(date);
    }
    
    this.rangeChange.emit({
      start: this.startDate,
      end: this.endDate
    });
  }

  private startNewRangeSelection(date: Date): void {
    this.startDate = new Date(date);
    this.endDate = null;
    this.selectionStep = 'end';
    this.updateInputValue();
  }

  private completeRangeSelection(date: Date): void {
    if (date < this.startDate!) {
      // If end date is before start date, swap them
      this.swapRangeDates(date);
      return;
    }
    this.endDate = new Date(date);
    this.selectionStep = 'start';
    this.updateInputValue();
  }

  private swapRangeDates(date: Date): void {
    this.endDate = this.startDate;
    this.startDate = new Date(date);
    this.selectionStep = 'start';
    this.updateInputValue();
  }

  // Format date using DatePipe
  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Format date and time together
  formatDateTime(date: Date): string {
    if (!this.enableTime) {
      return this.formatDate(date);
    }
    
    const dateStr = this.formatDate(date);
    const timeStr = this.datePipe.transform(date, this.timeFormat) || '';
    
    return `${dateStr} ${timeStr}`;
  }

  // Get start hours for range mode
  getStartHours(): string {
    if (!this.startDate) return this.use24Hour ? '00' : '12';
    
    const hours = this.startDate.getHours();
    return this.use24Hour ? 
      this.padZero(hours) : 
      this.padZero(this.get12Hour(hours));
  }

  // Get end hours for range mode
  getEndHours(): string {
    if (!this.endDate) return this.use24Hour ? '00' : '12';
    
    const hours = this.endDate.getHours();
    return this.use24Hour ? 
      this.padZero(hours) : 
      this.padZero(this.get12Hour(hours));
  }

  // Handle range time input change
  onRangeTimeInputChange(event: Event, field: RangeTimeField, type: RangeType): void {
    const input = event.target as HTMLInputElement;
    
    this.truncateInputToMaxDigits(input);
    const parsedValue = this.parseAndValidateRangeTimeInput(input, field);
    
    if (parsedValue === null) {
      return;
    }
    
    this.updateRangeTimeField(parsedValue, field, type);
  }

  private truncateInputToMaxDigits(input: HTMLInputElement): void {
    if (input.value.length > 2) {
      input.value = input.value.slice(0, 2);
    }
  }

  private parseAndValidateRangeTimeInput(input: HTMLInputElement, field: RangeTimeField): number | null {
    const parsedValue = parseInt(input.value, 10);
    
    if (isNaN(parsedValue)) {
      return null;
    }
    
    const { minValue, maxValue } = this.getRangeTimeFieldLimits(field);
    const constrainedValue = this.constrainTimeValue(parsedValue, minValue, maxValue, input);
    
    return constrainedValue;
  }

  private constrainTimeValue(value: number, minValue: number, maxValue: number, input: HTMLInputElement): number {
    if (value < minValue) {
      return minValue;
    }
    if (value > maxValue) {
      input.value = maxValue.toString();
      return maxValue;
    }
    return value;
  }

  private getRangeTimeFieldLimits(field: RangeTimeField): { minValue: number; maxValue: number } {
    const limits: { minValue: number; maxValue: number } = { minValue: 0, maxValue: 59 };
    
    if (field === 'hour') {
      limits.minValue = this.use24Hour ? 0 : 1;
      limits.maxValue = this.use24Hour ? 23 : 12;
    }
    
    return limits;
  }

  // Validate range time input
  validateRangeTimeInput(event: Event, field: RangeTimeField, type: RangeType): void {
    const input = event.target as HTMLInputElement;
    let value = parseInt(input.value, 10);
    
    if (isNaN(value)) {
      value = 0;
    }
    
    // Apply range limits based on field
    switch (field) {
      case 'hour':
        if (this.use24Hour) {
          value = Math.min(Math.max(value, 0), 23);
        } else {
          value = Math.min(Math.max(value, 1), 12);
        }
        break;
      case 'minute':
        value = Math.min(Math.max(value, 0), 59);
        break;
    }
    
    // Update the field with the valid value
    this.updateRangeTimeField(value, field, type);
    
    // Refresh display
    if (field === 'hour') {
      input.value = type === 'start' ? this.getStartHours() : this.getEndHours();
    } else {
      input.value = this.padZero(value);
    }
  }

  // Update time field for range
  updateRangeTimeField(value: number, field: RangeTimeField, type: RangeType): void {
    let dateToUpdate = type === 'start' ? this.startDate : this.endDate;
    
    if (!dateToUpdate) {
      dateToUpdate = new Date();
      if (type === 'start') {
        this.startDate = dateToUpdate;
      } else {
        this.endDate = dateToUpdate;
      }
    }
    
    switch (field) {
      case 'hour':
        value = this.convertHourTo24HourFormat(value, dateToUpdate);
        dateToUpdate.setHours(value);
        break;
      case 'minute':
        dateToUpdate.setMinutes(value);
        break;
    }
    
    this.updateInputValue();
    this.rangeChange.emit({
      start: this.startDate,
      end: this.endDate
    });
  }

  private convertHourTo24HourFormat(value: number, dateToUpdate: Date): number {
    if (this.use24Hour) {
      return value;
    }
    // In 12-hour mode, convert 12 to 0 for AM, keep 12 for PM
    const isPM = dateToUpdate.getHours() >= 12;
    if (value === 12) {
      return isPM ? 12 : 0;
    }
    if (isPM) {
      return value + 12;
    }
    return value;
  }

  // Set AM/PM for range time
  setRangeAmPm(period: AmPmPeriod, type: RangeType): void {
    let dateToUpdate = type === 'start' ? this.startDate : this.endDate;
    
    if (!dateToUpdate) {
      dateToUpdate = new Date();
      if (type === 'start') {
        this.startDate = dateToUpdate;
      } else {
        this.endDate = dateToUpdate;
      }
    }
    
    const hours = dateToUpdate.getHours();
    const isPM = hours >= 12;
    
    if (period === 'AM' && isPM) {
      dateToUpdate.setHours(hours - 12);
    } else if (period === 'PM' && !isPM) {
      dateToUpdate.setHours(hours + 12);
    }
    
    this.updateInputValue();
    this.rangeChange.emit({
      start: this.startDate,
      end: this.endDate
    });
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (
      this.isOpen && 
      !this.inline && 
      !this.datePickerContainer.nativeElement.contains(event.target)
    ) {
      this.isOpen = false;
    }
  }
  
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      // Re-calculate position when window is resized
      this.determineCalendarPosition();
    }
  }
  
  @HostListener('document:click')
  closeDropdowns(): void {
    this.monthDropdownOpen = false;
    this.yearDropdownOpen = false;
  }

  // Clear range date selection
  clearRangeDate(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    this.startDate = null;
    this.endDate = null;
    this.selectionStep = 'start';
    this.inputValue = '';
    this.rangeChange.emit({
      start: null,
      end: null
    });
    this.startRipple();
  }

  private applyDefaultViewDateIfNeeded(force = false): void {
    if (!this.defaultViewDate) {
      return;
    }

    if (this.selectedDate && !force) {
      return;
    }

    const defaultDate = new Date(this.defaultViewDate);
    if (isNaN(defaultDate.getTime())) {
      return;
    }

    this.currentMonth = defaultDate.getMonth();
    this.currentYear = defaultDate.getFullYear();
  }

  private scrollActiveYearIntoView(): void {
    if (!this.yearListContainer) {
      return;
    }

    const container = this.yearListContainer.nativeElement;
    const activeButton = container.querySelector<HTMLButtonElement>(`button[data-year="${this.currentYear}"]`);

    if (!activeButton) {
      return;
    }

    const offsetTop = activeButton.offsetTop;
    const offsetHeight = activeButton.offsetHeight;
    const targetScroll = offsetTop - container.clientHeight / 2 + offsetHeight / 2;
    container.scrollTop = Math.max(0, targetScroll);
  }
}